import SectionPanel from '@/components/SectionPanel';
import { Stack, Skeleton } from '@chakra-ui/react';
import type { DataTableColumnDef as ColumnDef, DataTableCellContext as CellContext } from 'src/components/DataTable';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FundraisingTotal } from 'src/api/FundraisingTotal';
import Content from 'src/components/Content';
import DataTable, { DataTableToolbar, amountHeader, amountCell } from 'src/components/DataTable';
import ReachLink from 'src/components/ReachLink';
import { reportService } from 'src/service/ReportService';
import { ProgressBarReact } from 'src/components/ProgressBar';
import WaitingSpinner from '@/components/WaitingSpinner';
import { Switch } from "@/components/ui/switch"
import { useI18n } from '@/i18n/I18nContext';


const DashboardFundraisingAll = () => {
    const { t } = useI18n();
    const [data, setData] = useState<FundraisingTotal[]>([]);
    const [active, setActive] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const navigate = useNavigate();

    const loadAllData = (): Promise<void> => {
        setIsLoading(true);
        return reportService.fundraisingAll()
            .then(r =>
                // overall aggregations with every year
                setData(r.overall.concat())
            )
            .finally(() => setIsLoading(false));
    }

    useEffect(() => {
        loadAllData();
    }, []);

    const onView = useCallback((id: number): void => {
        console.info(`viewing ${id}`);
        navigate(`/fundraising/projects/${id}`);
    }, [navigate]);

    const onFilter = (row: FundraisingTotal, value: string): boolean => {
        if (value === 'active') {
            return row.project.active;
        }
        return true;
    }

    const columns: ColumnDef<FundraisingTotal>[] = useMemo(
        () => [
            {
                id: 'project',
                header: 'Projekt',
                accessorKey: 'project.name',
                cell: (info) => {
                    let row = info.row.original;
                    return (<ReachLink to={`/projects/view/${row.project.id}`} name={row.project.name} tooltip='zu den Projektdetails' />);
                },
            },
            {
                id: 'budget',
                header: amountHeader({ name: ['Total FR Budget', '(BUD WK)'] }),
                accessorKey: 'balance.budgetTotal',
                cell: amountCell({})
            },
            {
                id: 'income',
                header: amountHeader({ name: 'Total Mittel', explanation: ['Total Mittel = Total institutionelle Beiträge + Zusagen', '*** (bis 31.12.2025 inklusiv Eigenmittel + Übertrag aus Vorgängerprojekt)'] }),
                accessorKey: 'coverage.incomeTotal',
                cell: amountCell({})
            },
            {
                id: 'open',
                header: amountHeader({ name: ['Offen Total', '(BUD WK)'], explanation: 'Offen Total = Total Mittel - Total Fundraising Budget (BUD WK)' }),
                accessorKey: 'coverage.coverage.total',
                cell: amountCell({ showSign: true })
            },
            {
                id: 'overallCoveragePercent',
                header: amountHeader({ name: ['Gesamtdeckung', '(BUD WK)'], explanation: 'Gesamtdeckung % = Total Mittel / Total Fundraising Budget (BUD WK)' }),
                accessorKey: 'coverage.coverage.percent',
                cell: (info: CellContext<FundraisingTotal, number>) => ProgressBarReact(info.getValue())
            },
            {
                id: 'avgCoverage',
                header: amountHeader({ name: ['Offen Total', '(Ø WK)'], explanation: 'Offener Betrag = Total Mittel - Total Fundraising Budget (Ø WK)' }),
                accessorKey: 'coverage.averageRateOpen.total',
                cell: amountCell({ showSign: true })
            },
            {
                id: 'avgCoveragePercent',
                header: amountHeader({ name: ['Gesamtdeckung', '(Ø WK)'], explanation: 'Jahresdeckung % = Total Mittel / Total Fundraising Budget (Ø WK)' }),
                accessorKey: 'coverage.averageRateOpen.percent',
                cell: (info: CellContext<FundraisingTotal, number>) => ProgressBarReact(info.getValue())
            },
        ], []
    );

    return (
        <Content title={t('reports.fundraising.title')} description={t('reports.fundraising.description')}>
            <Stack gap='6' minW='0'>
                <DataTableToolbar resultCount={data.filter(row => !active || row.project.active).length}>
                    <Switch id='activeOnly' colorPalette='blue' checked={active} onCheckedChange={event => setActive(event.checked)}>{t('projects.overview.activeOnly')}</Switch>
                </DataTableToolbar>
                <SectionPanel title={t('reports.fundraising.coverageByProject')}>
                    {isLoading ? (
                        <Stack py={5}>
                            <WaitingSpinner size='xl' />
                            <Skeleton height='20px' />
                            <Skeleton height='20px' />
                            <Skeleton height='20px' />
                        </Stack>
                    ) : (
                        <DataTable columns={columns} data={data} initialSortByColumn='project' initialSortByDesc={false}
                            hideColumnsOnXs={['income', 'coverage', 'coveragePercent', 'open', 'avgCoveragePercent']}
                            onFilter={onFilter} filter={active ? 'active' : ''}
                            onRowClick={row => onView(row.project.id)}
                        />
                    )}
                </SectionPanel>
            </Stack>
        </Content>
    );
}

export default DashboardFundraisingAll
